// SPDX-License-Identifier: AGPL-3.0-or-later

import {DEFAULT_GHOST_HEART_COLOR} from '@app/features/holiday/utils/heartReactionColor';
import {makeAutoObservable} from 'mobx';

const MAX_CONCURRENT_GHOSTS = 6;
export const MAX_CONCURRENT_BAT_FLOCKS = 6;

export type HolidayFlybyDirection = 'ltr' | 'rtl';
export type HolidayFlybyPath = 'cross' | 'rise';

export interface HolidayBatSprite {
	sizeRem: number;
	offsetXRem: number;
	offsetYRem: number;
	flapDelayMs: number;
	bobDelayMs: number;
	opacity: number;
	helixPath?: string;
	flapDurationMs?: number;
	waveLag?: number;
	flapCycles?: number;
	flapFm?: number;
	flapOffset?: number;
}

export interface HolidayBatFlockPlan {
	bats: ReadonlyArray<HolidayBatSprite>;
	direction: HolidayFlybyDirection;
	path: HolidayFlybyPath;
	bandFrom: number;
	bandTo: number;
	durationMs: number;
	scaleFrom: number;
	scaleTo: number;
	opacityFrom: number;
	opacityTo: number;
	flapDurationMs: number;
	originX?: number;
	originY?: number;
	riseXPx?: number;
	riseYPx?: number;
	waveCount?: number;
	waveAmpFrac?: number;
	wavePhase?: number;
	tiltRef?: number;
	startDelayMs?: number;
}

export interface HolidayCuteGhostParticle {
	id: number;
	kind: 'cute-ghost';
	x: number;
	y: number;
	heartColor: string;
}

export interface HolidayBatFlockParticle extends HolidayBatFlockPlan {
	id: number;
	kind: 'bat-flock';
}

export interface HolidaySidePeekParticle {
	id: number;
	kind: 'side-peek';
	channelId: string;
	packId: string;
	peekerId: string;
}

export type HolidayScareVariant = 'skeleton';

/** `grow` breaks through from a point; `pop` is already full size on the first frame. */
export type HolidayScareMode = 'grow' | 'pop';

export interface HolidayScareParticle {
	id: number;
	kind: 'scare';
	variant: HolidayScareVariant;
	mode: HolidayScareMode;
	/** Spawn time, so a sprite re-parented by a view change resumes mid-flight instead of replaying. */
	spawnedAt: number;
}

export type HolidayParticle =
	| HolidayCuteGhostParticle
	| HolidayBatFlockParticle
	| HolidaySidePeekParticle
	| HolidayScareParticle;

export type HolidayParticleKind = HolidayParticle['kind'];

/**
 * Appear small (60ms) → hold small (150ms) → spring to full (545ms) → hold full (1500ms) →
 * shrink away (365ms). The fade out trails the shrink rather than starting with it.
 */
export const SCARE_DURATION_MS = 2620;

export const SIDE_PEEK_DURATION_MS = 5000;
/** Matches `holiday-side-peek` rest keyframes (12%–73%). */
export const SIDE_PEEK_REST_START_MS = Math.round(SIDE_PEEK_DURATION_MS * 0.12);
export const SIDE_PEEK_REST_END_MS = Math.round(SIDE_PEEK_DURATION_MS * 0.73);

class HolidayState {
	particles: Array<HolidayParticle> = [];
	lastBatFlockAt: number | null = null;
	lastReactionBatAt: number | null = null;
	lastScareAt: number | null = null;
	batsDisplayLoopEnabled = false;
	private nextParticleId = 1;
	private pendingHeartColors: Array<string> = [];
	private sidePeekTimer: ReturnType<typeof setTimeout> | null = null;
	private scareTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<HolidayState, 'nextParticleId' | 'pendingHeartColors' | 'sidePeekTimer' | 'scareTimer'>(this, {
			nextParticleId: false,
			pendingHeartColors: false,
			sidePeekTimer: false,
			scareTimer: false,
		});
	}

	hasKind(kind: HolidayParticleKind): boolean {
		return this.particles.some((particle) => particle.kind === kind);
	}

	toggleBatsDisplayLoop(): boolean {
		this.batsDisplayLoopEnabled = !this.batsDisplayLoopEnabled;
		return this.batsDisplayLoopEnabled;
	}

	armHeartColor(color: string): void {
		if (this.pendingHeartColors.length >= MAX_CONCURRENT_GHOSTS) {
			this.pendingHeartColors.shift();
		}
		this.pendingHeartColors.push(color);
	}

	spawnCuteGhost(x: number, y: number, heartColor?: string): number {
		const ghosts = this.particles.filter((particle) => particle.kind === 'cute-ghost');
		if (ghosts.length >= MAX_CONCURRENT_GHOSTS) {
			const oldest = ghosts[0];
			if (oldest != null) {
				this.particles = this.particles.filter((particle) => particle.id !== oldest.id);
			}
		}
		const id = this.nextParticleId++;
		const resolvedHeartColor = heartColor ?? this.pendingHeartColors.shift() ?? DEFAULT_GHOST_HEART_COLOR;
		this.particles.push({id, kind: 'cute-ghost', x, y, heartColor: resolvedHeartColor});
		return id;
	}

	spawnBatFlock(plan: HolidayBatFlockPlan, now: number): number {
		const flocks = this.particles.filter((particle) => particle.kind === 'bat-flock');
		if (flocks.length >= MAX_CONCURRENT_BAT_FLOCKS) {
			const oldest = flocks[0];
			if (oldest != null) {
				this.particles = this.particles.filter((particle) => particle.id !== oldest.id);
			}
		}
		const id = this.nextParticleId++;
		this.particles.push({id, kind: 'bat-flock', ...plan});
		this.lastBatFlockAt = now;
		return id;
	}

	spawnSidePeek(packId: string, peekerId: string, channelId: string): number | null {
		if (this.hasKind('side-peek')) return null;
		const id = this.nextParticleId++;
		this.particles.push({id, kind: 'side-peek', channelId, packId, peekerId});
		this.scheduleSidePeekRemoval(id);
		return id;
	}

	/**
	 * One scare at a time, and the lifetime timer runs from spawn. A scare deliberately survives a
	 * channel switch, so nothing here is keyed to a channel.
	 */
	spawnScare(variant: HolidayScareVariant, now: number, mode: HolidayScareMode = 'grow'): number | null {
		if (this.hasKind('scare')) return null;
		const id = this.nextParticleId++;
		this.particles.push({id, kind: 'scare', variant, mode, spawnedAt: now});
		this.lastScareAt = now;
		this.scheduleScareRemoval(id);
		return id;
	}

	removeKind(kind: HolidayParticleKind): void {
		if (kind === 'side-peek') this.clearSidePeekTimer();
		if (kind === 'scare') this.clearScareTimer();
		this.particles = this.particles.filter((particle) => particle.kind !== kind);
	}

	removeParticle(id: number): void {
		const particle = this.particles.find((item) => item.id === id);
		if (particle?.kind === 'side-peek') this.clearSidePeekTimer();
		if (particle?.kind === 'scare') this.clearScareTimer();
		this.particles = this.particles.filter((item) => item.id !== id);
	}

	private scheduleSidePeekRemoval(id: number): void {
		this.clearSidePeekTimer();
		this.sidePeekTimer = setTimeout(() => {
			this.sidePeekTimer = null;
			this.removeParticle(id);
		}, SIDE_PEEK_DURATION_MS);
	}

	private clearSidePeekTimer(): void {
		if (this.sidePeekTimer == null) return;
		clearTimeout(this.sidePeekTimer);
		this.sidePeekTimer = null;
	}

	private scheduleScareRemoval(id: number): void {
		this.clearScareTimer();
		this.scareTimer = setTimeout(() => {
			this.scareTimer = null;
			this.removeParticle(id);
		}, SCARE_DURATION_MS);
	}

	private clearScareTimer(): void {
		if (this.scareTimer == null) return;
		clearTimeout(this.scareTimer);
		this.scareTimer = null;
	}
}

export default new HolidayState();
